/**
 * Utility functions for BorrowerBank form logic
 */

import { Dispatch } from "@reduxjs/toolkit";
import {
  setBankNameRedux,
  setBankCodeRedux,
  setAccountHolderNameRedux,
  setAccountNoRedux,
  setSwiftCodeReduct,
  setAccountTypeRedux,
  setBankAddressRedux,
  setBankCountryRedux,
  setCurrencyRedux,
} from "../../../features/merchant/bankSlice";
import { BankFormData, BankFormErrors } from "../types";
import { VALIDATION_RULES, TOAST_MESSAGES } from "../constants";
import { toast } from "react-toastify";

/**
 * Hydrates bank data into Redux store
 * Uses unwrapped payload pattern for efficiency
 */
export const hydrateBankDataToRedux = (
  dispatch: Dispatch,
  data: BankFormData
) => {
  dispatch(setBankNameRedux(data.bankName));
  dispatch(setBankCodeRedux(data.bankCode || ""));
  dispatch(setAccountNoRedux(data.accountNumber));
  dispatch(setAccountHolderNameRedux(data.accountHolderName));
  dispatch(setAccountTypeRedux(data.accountType));
  dispatch(setSwiftCodeReduct(data.swiftCode));
  dispatch(setBankAddressRedux(data.bankAddress));
  dispatch(setBankCountryRedux(data.bankCountry));
  dispatch(setCurrencyRedux(data.currency));
};

/**
 * Hydrates bank data into local state setters
 */
export const hydrateBankDataToState = (
  data: BankFormData,
  setters: {
    setBankName: (value: string) => void;
    setAccountNumber: (value: string) => void;
    setAccountType: (value: string) => void;
    setSwiftCode: (value: string) => void;
    setAccountHolderName: (value: string) => void;
    setBankAddress: (value: string) => void;
    setBankCountry: (value: string) => void;
    setCurrency: (value: string) => void;
  }
) => {
  setters.setBankName(data.bankName || "");
  setters.setAccountNumber(data.accountNumber || "");
  setters.setAccountType(data.accountType || "");
  setters.setSwiftCode(data.swiftCode || "");
  setters.setAccountHolderName(data.accountHolderName || "");
  setters.setBankAddress(data.bankAddress || "");
  setters.setBankCountry(data.bankCountry || "");
  setters.setCurrency(data.currency || "");
};

/**
 * Validates all bank form fields
 */
export const validateBankForm = (
  formData: BankFormData,
  companyName?: string
): { valid: boolean; errors: BankFormErrors } => {
  const errors = { ...VALIDATION_RULES };
  let valid = true;

  // Check required fields
  (Object.keys(formData) as Array<keyof BankFormData>).forEach((field) => {
    if (!formData[field]) {
      valid = false;
    } else {
      errors[field] = "";
    }
  });

  // Special validation for account holder name vs company name
  if (companyName && formData.accountHolderName !== companyName) {
    valid = false;
    toast.warn(TOAST_MESSAGES.ACCOUNT_HOLDER_WARNING);
  }

  return { valid, errors };
};

/**
 * Validates account holder name matches company name
 * Used in saveDraft to prevent saving mismatched data
 * Returns boolean only - error display is handled by the component
 */
export const validateAccountHolderName = (
  accountHolderName: string,
  companyName?: string
): boolean => {
  if (accountHolderName && companyName && accountHolderName !== companyName) {
    return false;
  }
  return true;
};